import {ToastProps} from "@intuitionrobotics/thunderstorm/app-frontend/modules/toaster/BaseToaster";
import {Toast_Model, ToastType} from "@intuitionrobotics/thunderstorm/app-frontend/modules/toaster/ToasterModule";
import * as React from "react";
import {CSSProperties} from "react";
import * as emotion from "emotion";
import {BaseComponent} from "@intuitionrobotics/thunderstorm/frontend";
import {COLOR_charcoalGray, COLOR_darkGray, COLOR_lightGray, COLOR_red, COLOR_veryLightGray, COLOR_white} from "@styles/colors";
import {_clearTimeout, _setTimeout, generateHex, TimerHandler} from "@intuitionrobotics/ts-common";

const svg = (path: React.ReactNode) =>
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{path}</svg>;

// Accent colours: AA contrast with the white icon glyph; neutral falls back to the app charcoal.
const ACCENTS: { [type: string]: { color: string, icon: React.ReactNode } } = {
    [ToastType.success]: {color: "#2e7d32", icon: svg(<path d="M2.5 7.5l3 3 6-6.5"/>)},
    [ToastType.error]: {color: COLOR_red, icon: svg(<path d="M7 3v4.5M7 10.5v.01"/>)},
    [ToastType.info]: {color: "#0277bd", icon: svg(<path d="M7 6.5v4M7 3.5v.01"/>)},
    default: {color: COLOR_charcoalGray, icon: svg(<path d="M7 6.5v4M7 3.5v.01"/>)}
};

const slideIn = emotion.keyframes({
    from: {opacity: 0, transform: "translateY(8px)"},
    to: {opacity: 1, transform: "translateY(0)"}
});

const stackClass = emotion.css({
    position: "fixed",
    zIndex: 9999,
    display: "flex",
    gap: 10,
    padding: 16,
    maxWidth: "100vw",
    boxSizing: "border-box",
    pointerEvents: "none"
});

const toastClass = emotion.css({
    pointerEvents: "auto",
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    boxSizing: "border-box",
    minWidth: 280,
    maxWidth: "min(480px, calc(100vw - 32px))",
    padding: "12px 12px 12px 14px",
    background: COLOR_white,
    color: COLOR_darkGray,
    borderRadius: 8,
    borderLeft: "4px solid",
    boxShadow: "0 6px 20px rgba(0, 0, 0, 0.16), 0 1px 3px rgba(0, 0, 0, 0.12)",
    fontSize: 14,
    lineHeight: 1.45,
    animation: `${slideIn} 180ms ease-out`,
    "@media (max-width: 480px)": {minWidth: 0, width: "calc(100vw - 32px)"}
});

const iconClass = emotion.css({
    flex: "none",
    width: 22,
    height: 22,
    marginTop: 1,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
});

const contentClass = emotion.css({
    flex: 1,
    minWidth: 0,
    overflowWrap: "anywhere",
    maxHeight: "40vh",
    overflowY: "auto",
    alignSelf: "center"
});

const closeClass = emotion.css({
    flex: "none",
    display: "flex",
    padding: 4,
    margin: "-2px 0 0 0",
    border: 0,
    borderRadius: 4,
    background: "transparent",
    color: COLOR_veryLightGray,
    cursor: "pointer",
    "&:hover": {background: COLOR_lightGray, color: COLOR_darkGray}
});

type ToastWithId = Toast_Model & {
    _id: string
};

export class MyToaster
    extends BaseComponent<ToastProps, { models: ToastWithId[] }> {

    constructor(props: ToastProps) {
        super(props);
        this.state = {models: []}
    }

    __showToast = (model?: Toast_Model): void => {
        if (!model)
            return this.setState({models: []});

        const realModel = {...model, _id: generateHex(8)};
        this.setState(state => {
            const models = state.models;
            models.push(realModel)
            return {models};
        });

        const duration = model.duration;
        if (duration <= 0)
            return;

        this.debounce(this.hideToast, 'close_' + realModel._id, duration, realModel);
    };

    debounce(handler: TimerHandler, key: string, ms = 0, ...args: any[]) {
        // @ts-ignore
        _clearTimeout(this.timeoutMap[key]);
        // @ts-ignore
        this.timeoutMap[key] = _setTimeout(handler, ms, args);
    }


    private hideToast = (_models: ToastWithId[]) => {
        this.setState(state => {
            const models: ToastWithId[] = state.models;
            const idx = models.findIndex(m => _models[0]?._id === m._id);
            if (idx !== -1)
                models.splice(idx, 1);
            return {models};
        })
    };

    renderToast(toast: ToastWithId) {
        const accent = ACCENTS[toast.type] || ACCENTS.default;
        return (
            <div key={toast._id}
                 role={toast.type === ToastType.error ? "alert" : "status"}
                 className={`${toastClass} ${toast.className || ""}`}
                 style={{borderLeftColor: accent.color, ...toast.style}}>
                <div className={iconClass} style={{background: accent.color}}>{accent.icon}</div>
                <div className={contentClass}>
                    {typeof toast.content === "string" ?
                        <div dangerouslySetInnerHTML={{__html: toast.content}}/> : toast.content}
                </div>
                {this.renderClose(toast)}
            </div>
        );
    }

    render() {
        const toasts: ToastWithId[] = this.state.models;
        if (!toasts.length)
            return null;

        // One fixed flex container per screen position so toasts stack without overlapping, whatever their height.
        const groups: { [pos: string]: ToastWithId[] } = {};
        toasts.forEach(t => {
            const key = `${t.positionVertical === "top" ? "top" : "bottom"}-${t.positionHorizontal || "right"}`;
            (groups[key] = groups[key] || []).push(t);
        });

        return <>{Object.keys(groups).map(key => {
            const [v, h] = key.split("-");
            const style: CSSProperties = {
                [v]: 0,
                left: h === "left" ? 0 : h === "center" ? "50%" : "auto",
                right: h === "right" ? 0 : "auto",
                transform: h === "center" ? "translateX(-50%)" : "none",
                flexDirection: v === "top" ? "column" : "column-reverse",
                alignItems: h === "left" ? "flex-start" : h === "right" ? "flex-end" : "center"
            };
            return <div key={key} className={stackClass} style={style}>{groups[key].map(t => this.renderToast(t))}</div>;
        })}</>;
    }

    private renderClose(toast: ToastWithId) {
        return <button type="button" aria-label="Dismiss" onClick={() => this.hideToast([toast])} className={closeClass}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M3 3l8 8M11 3l-8 8"/></svg>
        </button>
    }
}
